import { ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { TokenStatus, LocationTokenStatus } from '@/hooks/useTokenHealth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMinutes(minutes: number): string {
  const abs = Math.abs(minutes)
  if (abs < 60) return `${abs}m`
  const hours = Math.floor(abs / 60)
  const mins = abs % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

function getTooltipText(status: TokenStatus, detail?: LocationTokenStatus): string {
  switch (status) {
    case 'healthy':
      return detail
        ? `Token valid — expires in ${formatMinutes(detail.minutesUntilExpiry)}`
        : 'Token valid'
    case 'expiring_soon':
      return detail
        ? `Token expiring soon — expires in ${formatMinutes(detail.minutesUntilExpiry)}`
        : 'Token expiring soon'
    case 'expired':
      return detail
        ? `Token expired ${formatMinutes(detail.minutesUntilExpiry)} ago — revalidate or reinstall`
        : 'Token expired — revalidate or reinstall'
    case 'unknown':
    default:
      return 'Token status unknown — no OAuth token found for this location'
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  readonly status: TokenStatus
  readonly detail?: LocationTokenStatus
  readonly size?: 'sm' | 'md'
}

const ICON_CLASSES: Record<TokenStatus, string> = {
  healthy: 'text-green-500',
  expiring_soon: 'text-yellow-500',
  expired: 'text-red-500',
  unknown: 'text-gray-400',
}

export function TokenStatusBadge({ status, detail, size = 'sm' }: Props) {
  const sizeClass = size === 'md' ? 'h-5 w-5' : 'h-4 w-4'
  const iconClass = `${sizeClass} ${ICON_CLASSES[status]} flex-shrink-0`
  const tooltipText = getTooltipText(status, detail)

  const Icon = {
    healthy: ShieldCheck,
    expiring_soon: ShieldAlert,
    expired: ShieldX,
    unknown: ShieldQuestion,
  }[status]

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center">
            <Icon className={iconClass} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
